import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { X, Loader2, Send, FileText, ShieldAlert } from "lucide-react";
import { EmailConfirmModal } from "@/components/EmailConfirmModal";
import { useEmailGuard } from "@/hooks/useEmailGuard";

// Freelancer Job Assignment Tool Phase 6 "Review + Send" refinement (2026-02) — single-
// freelancer review modal shown from the Statements tab's "Review" button. Renders the exact
// AI-grounded intro + inline-styled HTML the backend will actually email (via
// POST /v1/statements/compose, no side effects), lets finance edit the intro, then commits to
// EXACTLY ONE of two explicit actions: "Save to finance@ Drafts" (unchanged draft-only
// behaviour) or "Send" (single-freelancer only, gated behind its own recipient+total
// confirmation step — never triggered automatically).
// Part 8 Cursor review item 6 (2026-10): a 403 email_send_blocked now shows the actual gate
// reason instead of the generic "Try again" toast — retrying changes nothing until the
// founder fixes the underlying EMAIL_SENDS_DISABLED/EMAIL_ALLOWLIST setting, so the old
// message was actively misleading.
function emailBlockedMessage(reason) {
  const r = reason || "";
  if (r.includes("email_sends_disabled")) return "Email sending is currently disabled (EMAIL_SENDS_DISABLED). Ask the founder to enable it in Settings.";
  if (r.includes("email_allowlist")) return "This recipient isn't on the EMAIL_ALLOWLIST. Ask the founder to add them in Settings \u2192 Email Health.";
  if (r.includes("blocked_malformed_recipient")) return "This recipient's email address looks malformed — fix it on the freelancer's profile first.";
  if (r.includes("blocked_non_production")) return "This is a non-production environment — only allowlisted test recipients can receive email here.";
  return `Email send blocked: ${r || "unknown reason"}`;
}

export function StatementReviewModal({ month, monthLabel, freelancerId, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [intro, setIntro] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmingSend, setConfirmingSend] = useState(false);
  const [sending, setSending] = useState(false);
  const guard = useEmailGuard();

  const compose = (introOverride) => {
    const body = { month, freelancer_id: freelancerId };
    if (introOverride !== undefined) body.intro_override = introOverride;
    return api.post("/v1/statements/compose", body).then((r) => {
      setPreview(r.data);
      setIntro(r.data.intro);
    });
  };

  useEffect(() => {
    setLoading(true);
    compose().catch(() => setError("Couldn't load this statement for review.")).finally(() => setLoading(false));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshPreview = () => {
    setRefreshing(true);
    setError(null);
    compose(intro).catch(() => setError("Couldn't refresh the preview.")).finally(() => setRefreshing(false));
  };

  const saveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await api.post("/v1/statements/generate", { month, freelancer_ids: [freelancerId], intro_override: intro });
      if (r.data.skipped?.length) throw new Error(r.data.skipped[0].reason);
      toast.success(`Draft saved to finance@ for ${preview.freelancer_name}`);
      onDone();
      onClose();
    } catch (e) {
      if (e?.response?.status === 503) {
        setError(e.response.data?.detail?.reason === "finance_gmail_not_connected" ? "Finance Gmail is not connected. Connect it in Administration first." : "Gmail isn't configured.");
      } else {
        toast.error("Couldn't save draft. Try again");
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmSend = async () => {
    setSending(true);
    setError(null);
    try {
      const r = await api.post("/v1/statements/send", { month, freelancer_id: freelancerId, intro });
      // Email Send Guard (2026-02): a statement send is always a single external recipient
      // (the freelancer's own address) — nothing is sent at all until the shared "This will
      // email N people" modal below is confirmed with a server-issued token.
      if (guard.intercept(r.data, async (typedCount) => {
        await api.post("/v1/statements/send", { month, freelancer_id: freelancerId, intro, confirmation_token: r.data.confirmation_token, confirmed_count: typedCount });
        toast.success(`Statement sent to ${preview.email}`);
        onDone();
        onClose();
      })) return;
      toast.success(`Statement sent to ${preview.email}`);
      onDone();
      onClose();
    } catch (e) {
      if (e?.response?.status === 503) {
        setError(e.response.data?.detail?.reason === "finance_gmail_not_connected" ? "Finance Gmail is not connected. Connect it in Administration first." : "Gmail isn't configured.");
      } else if (e?.response?.status === 403 && e.response.data?.detail?.code === "email_send_blocked") {
        setError(emailBlockedMessage(e.response.data.detail.reason));
      } else {
        toast.error("Couldn't send statement. Try again");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="statement-review-modal" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="statement-review-modal-backdrop" />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-hairline bg-paper p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-editorial text-lg font-bold text-ink">Review Statement</div>
          <button data-testid="statement-review-close-btn" onClick={onClose} aria-label="Close" className="text-faint hover:text-ink transition-colors"><X className="h-4 w-4" /></button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-faint" data-testid="statement-review-loading">Loading…</div>
        ) : !preview ? (
          <div className="py-6 text-center text-sm text-red-600" data-testid="statement-review-load-error">Couldn't load this statement.</div>
        ) : (
          <>
            <div className="flex items-center justify-between text-sm">
              <div>
                <div className="text-ink font-medium">{preview.freelancer_name}</div>
                <div className="text-faint text-xs">{preview.email || "No registered email"} · {preview.month_label}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-faint">Total</div>
                <div className="font-mono text-lg font-semibold text-ink" data-testid="statement-review-total">{formatMoney(preview.total_amount)}</div>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-faint">Intro (editable)</label>
              <textarea
                data-testid="statement-review-intro-textarea" rows={3} value={intro}
                onChange={(e) => setIntro(e.target.value)}
                className="w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink focus:border-brand outline-none transition-colors resize-none"
              />
              <button data-testid="statement-review-refresh-btn" onClick={refreshPreview} disabled={refreshing}
                className="inline-flex items-center gap-1.5 text-xs text-ink-soft hover:text-ink disabled:opacity-50 transition-colors">
                {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Refresh preview with this intro
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-faint">Email preview</label>
              <iframe
                data-testid="statement-review-html-preview-frame" title="Statement email preview" sandbox=""
                srcDoc={preview.html_body}
                className="w-full h-80 rounded-md border border-hairline bg-white"
              />
            </div>

            {error && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-600" data-testid="statement-review-error">{error}</div>}

            {confirmingSend ? (
              <div className="rounded-lg border border-brand/40 bg-brand/10 p-4 space-y-3" data-testid="statement-review-send-confirm">
                <div className="flex items-start gap-2 text-sm text-ink">
                  <ShieldAlert className="h-4 w-4 text-brand mt-0.5 shrink-0" />
                  <div>
                    Send <span className="font-semibold">{formatMoney(preview.total_amount)}</span> statement to
                    <span className="font-mono"> {preview.email}</span>? This delivers immediately via finance@. It cannot be edited afterward.
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button data-testid="statement-review-cancel-send-btn" onClick={() => setConfirmingSend(false)} disabled={sending}
                    className="rounded-md px-3 py-1.5 text-xs text-ink-soft hover:text-ink transition-colors">Cancel</button>
                  <button data-testid="statement-review-confirm-send-btn" onClick={confirmSend} disabled={sending}
                    className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-ink hover:bg-brand/90 disabled:opacity-50 transition-colors">
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Confirm &amp; Send
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end gap-2 pt-1">
                <button data-testid="statement-review-save-draft-btn" onClick={saveDraft} disabled={saving || sending}
                  className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-3 py-1.5 text-xs text-ink-soft hover:border-brand hover:text-ink disabled:opacity-50 transition-colors">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Save to finance@ Drafts
                </button>
                <button data-testid="statement-review-send-btn" onClick={() => setConfirmingSend(true)} disabled={saving || !preview.email}
                  className="inline-flex items-center gap-1.5 rounded-md border border-brand/40 bg-brand/10 px-3 py-1.5 text-xs font-medium text-ink hover:bg-brand/20 disabled:opacity-50 transition-colors">
                  <Send className="h-3.5 w-3.5" /> Send
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <EmailConfirmModal open={guard.open} preview={guard.preview} busy={guard.busy} onConfirm={guard.confirm} onCancel={guard.cancel} />
    </div>
  );
}
